/*
 * tc-giveaway.js — Giveaway slideshow ("filmstrip").
 * One <tc-giveaway> per section. Slides are blocks; the background strip
 * moves at half speed behind them so the whole thing reads as one long image.
 * Autoplay pauses on hover/focus, off-screen, in a hidden tab, and for
 * visitors who prefer reduced motion. Swipe + arrow keys + dots + arrows.
 */
(() => {
  if (customElements.get('tc-giveaway')) return;

  class TcGiveaway extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gw-viewport]');
      this.track = this.querySelector('[data-gw-track]');
      this.bg = this.querySelector('[data-gw-bg]');
      this.slides = Array.from(this.querySelectorAll('[data-gw-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-gw-dot]'));
      this.n = this.slides.length;
      this.index = 0;
      this.inView = true;
      this.userPaused = false;
      this.hovering = false;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.reduced ? 0 : parseInt(this.dataset.autoplay || '0', 10) || 0;
      this.style.setProperty('--gw-autoplay', this.autoplay + 'ms');
      if (this.reduced) this.style.setProperty('--gw-transition', '0ms');
      this.dataset.autoplayActive = this.autoplay ? 'true' : 'false';

      this.bindControls();
      this.bindSwipe();
      this.bindCountdowns();
      this.bindEditor();

      this.go(0);

      this.onVisibility = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay());
      document.addEventListener('visibilitychange', this.onVisibility);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.inView ? this.startAutoplay() : this.stopAutoplay();
        }, { threshold: 0.2 });
        this.io.observe(this);
      }

      this.onResize = () => this.applyTransform(true);
      window.addEventListener('resize', this.onResize);

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('resize', this.onResize);
      if (this.io) this.io.disconnect();
      if (this.tick) clearInterval(this.tick);
    }

    /* ---------- navigation ---------- */
    go(i) {
      if (!this.n) return;
      this.index = ((i % this.n) + this.n) % this.n;
      this.applyTransform();

      this.slides.forEach((slide, k) => {
        const active = k === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
          if (active) {
            if (el.dataset.gwTabindex !== undefined) {
              el.setAttribute('tabindex', el.dataset.gwTabindex);
            } else {
              el.removeAttribute('tabindex');
            }
          } else {
            if (el.hasAttribute('tabindex') && el.dataset.gwTabindex === undefined) {
              el.dataset.gwTabindex = el.getAttribute('tabindex');
            }
            el.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, k) => {
        const active = k === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.restartDotFill();
    }

    applyTransform(instant) {
      if (!this.track) return;
      if (instant) this.track.style.transition = 'none';
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      if (this.bg) {
        const w = this.viewport ? this.viewport.clientWidth : this.clientWidth;
        if (instant) this.bg.style.transition = 'none';
        this.bg.style.transform = `translateX(-${Math.round(this.index * w * 0.5)}px)`;
      }
      if (instant) {
        // force reflow, then restore transitions
        void this.track.offsetWidth;
        this.track.style.transition = '';
        if (this.bg) this.bg.style.transition = '';
      }
    }

    next() { this.go(this.index + 1); }
    prev() { this.go(this.index - 1); }

    /* ---------- autoplay ---------- */
    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || this.n < 2) return;
      if (!this.inView || this.userPaused || this.hovering || document.hidden) return;
      this.timer = setInterval(() => this.next(), this.autoplay);
      this.classList.remove('is-paused');
      this.restartDotFill();
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      this.classList.add('is-paused');
    }

    restartDotFill() {
      const fill = this.querySelector('.tc-gw__dot-btn.is-active .tc-gw__dot-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }

    interact(fn) {
      fn();
      // a click restarts the clock so the next auto-advance is a full interval away
      this.startAutoplay();
    }

    /* ---------- controls ---------- */
    bindControls() {
      const prev = this.querySelector('[data-gw-prev]');
      const nextBtn = this.querySelector('[data-gw-next-arrow]');
      if (prev) prev.addEventListener('click', () => this.interact(() => this.prev()));
      if (nextBtn) nextBtn.addEventListener('click', () => this.interact(() => this.next()));

      this.dots.forEach((dot, k) => dot.addEventListener('click', () => this.interact(() => this.go(k))));

      // in-slide "next" buttons (e.g. "How it works")
      this.querySelectorAll('[data-gw-next]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.interact(() => this.next());
        });
      });

      this.addEventListener('mouseenter', () => { this.hovering = true; this.stopAutoplay(); });
      this.addEventListener('mouseleave', () => { this.hovering = false; this.startAutoplay(); });
      this.addEventListener('focusin', () => { this.hovering = true; this.stopAutoplay(); });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) { this.hovering = false; this.startAutoplay(); }
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.interact(() => this.next()); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.interact(() => this.prev()); }
      });
    }

    /* ---------- swipe / drag ---------- */
    bindSwipe() {
      if (!this.viewport || !this.track) return;
      let startX = 0, startY = 0, dx = 0, dragging = false, decided = false, horizontal = false;

      const onDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        startX = e.clientX; startY = e.clientY; dx = 0; dragging = true; decided = false; horizontal = false;
        this.stopAutoplay();
      };
      const onMove = (e) => {
        if (!dragging) return;
        dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (!decided && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          decided = true; horizontal = Math.abs(dx) > Math.abs(dy);
          if (horizontal) this.track.style.transition = 'none';
        }
        if (decided && horizontal) {
          this.track.style.transform = `translateX(calc(-${this.index * 100}% + ${dx}px))`;
          this.dragged = true;
        }
      };
      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        this.track.style.transition = '';
        if (horizontal && Math.abs(dx) > 40) {
          dx < 0 ? this.next() : this.prev();
        } else {
          this.applyTransform();
        }
        setTimeout(() => { this.dragged = false; }, 50);
        this.startAutoplay();
      };

      this.viewport.addEventListener('pointerdown', onDown);
      this.viewport.addEventListener('pointermove', onMove);
      this.viewport.addEventListener('pointerup', onUp);
      this.viewport.addEventListener('pointercancel', onUp);
      this.viewport.addEventListener('pointerleave', onUp);
      // swallow the click that follows a real drag so links don't fire
      this.viewport.addEventListener('click', (e) => { if (this.dragged) { e.preventDefault(); e.stopPropagation(); } }, true);
      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
    }

    /* ---------- countdown ---------- */
    bindCountdowns() {
      const boxes = Array.from(this.querySelectorAll('[data-gw-countdown]'));
      if (!boxes.length) return;
      const pad = (v) => String(Math.max(0, v)).padStart(2, '0');
      const render = () => {
        const now = Date.now();
        boxes.forEach((box) => {
          const end = Date.parse(box.dataset.end || '');
          if (isNaN(end)) return;
          let diff = Math.floor((end - now) / 1000);
          const closed = diff <= 0;
          if (closed) diff = 0;
          const d = Math.floor(diff / 86400);
          const h = Math.floor((diff % 86400) / 3600);
          const m = Math.floor((diff % 3600) / 60);
          const s = diff % 60;
          const set = (sel, val) => { const el = box.querySelector(sel); if (el && el.textContent !== val) el.textContent = val; };
          set('[data-gw-days]', pad(d));
          set('[data-gw-hours]', pad(h));
          set('[data-gw-mins]', pad(m));
          set('[data-gw-secs]', pad(s));
          box.classList.toggle('is-closed', closed);
          const closedEl = box.parentElement && box.parentElement.querySelector('[data-gw-closed]');
          if (closedEl) closedEl.hidden = !closed;
        });
      };
      render();
      this.tick = setInterval(render, 1000);
    }

    /* ---------- theme editor ---------- */
    bindEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      document.addEventListener('shopify:block:select', (e) => {
        const slide = e.target && e.target.closest ? e.target.closest('[data-gw-slide]') : null;
        if (!slide || !this.contains(slide)) return;
        this.userPaused = true;
        this.stopAutoplay();
        this.go(this.slides.indexOf(slide));
      });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (!this.contains(e.target)) return;
        this.userPaused = false;
        this.startAutoplay();
      });
    }
  }

  customElements.define('tc-giveaway', TcGiveaway);
})();